// City config for programmatic-SEO mentor landing pages (/m/{exam}-mentor-in-{city}).
// Hardcoded initial list. Keep in sync with the functions-side copy used by the
// prerender + sitemap: apps/functions/src/http/seoLandingConfig.ts.
export interface City {
  slug: string;          // 'pune'
  displayName: string;   // 'Pune' (also how mentor.city is stored)
  aliases?: string[];    // alternate names a mentor may have typed ('Bengaluru' for Bangalore)
  state: string;         // 'Maharashtra'
  tier: 1 | 2 | 3;
  lat?: number;
  lng?: number;
}

// True if a mentor's stored city string matches this city's display name or any alias
// (case-insensitive). Used for filtering mentors onto a landing page.
export function cityMatchesName(city: City, mentorCity: string | null | undefined): boolean {
  if (!mentorCity) return false;
  const n = mentorCity.trim().toLowerCase();
  return [city.displayName, ...(city.aliases ?? [])].some((c) => c.toLowerCase() === n);
}

export const CITIES: City[] = [
  { slug: 'mumbai', displayName: 'Mumbai', aliases: ['Bombay'], state: 'Maharashtra', tier: 1, lat: 19.0760, lng: 72.8777 },
  { slug: 'delhi', displayName: 'Delhi', state: 'Delhi', tier: 1, lat: 28.6139, lng: 77.2090 },
  { slug: 'bangalore', displayName: 'Bangalore', aliases: ['Bengaluru', 'Bangaluru'], state: 'Karnataka', tier: 1, lat: 12.9716, lng: 77.5946 },
  { slug: 'chennai', displayName: 'Chennai', aliases: ['Madras'], state: 'Tamil Nadu', tier: 1, lat: 13.0827, lng: 80.2707 },
  { slug: 'hyderabad', displayName: 'Hyderabad', state: 'Telangana', tier: 1, lat: 17.3850, lng: 78.4867 },
  { slug: 'pune', displayName: 'Pune', state: 'Maharashtra', tier: 1, lat: 18.5204, lng: 73.8567 },
  { slug: 'kolkata', displayName: 'Kolkata', aliases: ['Calcutta'], state: 'West Bengal', tier: 1, lat: 22.5726, lng: 88.3639 },
  { slug: 'ahmedabad', displayName: 'Ahmedabad', state: 'Gujarat', tier: 1, lat: 23.0225, lng: 72.5714 },
  { slug: 'lucknow', displayName: 'Lucknow', state: 'Uttar Pradesh', tier: 2 },
  { slug: 'jaipur', displayName: 'Jaipur', state: 'Rajasthan', tier: 2 },
  { slug: 'indore', displayName: 'Indore', state: 'Madhya Pradesh', tier: 2 },
  { slug: 'bhopal', displayName: 'Bhopal', state: 'Madhya Pradesh', tier: 2 },
  { slug: 'patna', displayName: 'Patna', state: 'Bihar', tier: 2 },
  { slug: 'chandigarh', displayName: 'Chandigarh', state: 'Punjab', tier: 2 },
  { slug: 'coimbatore', displayName: 'Coimbatore', state: 'Tamil Nadu', tier: 2 },
  { slug: 'nagpur', displayName: 'Nagpur', state: 'Maharashtra', tier: 2 },
  { slug: 'kanpur', displayName: 'Kanpur', state: 'Uttar Pradesh', tier: 2 },
  { slug: 'visakhapatnam', displayName: 'Visakhapatnam', state: 'Andhra Pradesh', tier: 2 },
  { slug: 'surat', displayName: 'Surat', state: 'Gujarat', tier: 2 },
  { slug: 'vadodara', displayName: 'Vadodara', state: 'Gujarat', tier: 2 },
  { slug: 'kochi', displayName: 'Kochi', state: 'Kerala', tier: 2 },
  { slug: 'thiruvananthapuram', displayName: 'Thiruvananthapuram', aliases: ['Trivandrum'], state: 'Kerala', tier: 2 },
  { slug: 'bhubaneswar', displayName: 'Bhubaneswar', state: 'Odisha', tier: 2 },
  { slug: 'dehradun', displayName: 'Dehradun', state: 'Uttarakhand', tier: 2 }, // Defence hub
  { slug: 'guwahati', displayName: 'Guwahati', state: 'Assam', tier: 2 },
  { slug: 'ranchi', displayName: 'Ranchi', state: 'Jharkhand', tier: 2 },
  { slug: 'allahabad', displayName: 'Prayagraj', aliases: ['Allahabad'], state: 'Uttar Pradesh', tier: 2 }, // UPSC hub
  { slug: 'rohtak', displayName: 'Rohtak', state: 'Haryana', tier: 2 }, // SSC/defence hub
  { slug: 'jodhpur', displayName: 'Jodhpur', state: 'Rajasthan', tier: 2 },
  { slug: 'noida', displayName: 'Noida', state: 'Uttar Pradesh', tier: 2 },
  { slug: 'gurgaon', displayName: 'Gurgaon', aliases: ['Gurugram'], state: 'Haryana', tier: 2 },
];

export const CITY_BY_SLUG: Record<string, City> = Object.fromEntries(CITIES.map((c) => [c.slug, c]));
